import type {
  AdsWeekAccountTypeWeekParams,
  AdsWeekAccountTypeWeekResponse,
  AdsWeekCustomerWeekChangePageParams,
  AdsWeekCustomerWeekChangePageResponse,
  AdsWeekCustomerWeekChangeTopParams,
  AdsWeekCustomerWeekChangeTopResponse,
  AdsWeekIndustryWeekParams,
  AdsWeekIndustryWeekResponse,
} from './types/week-report'

import { defineApi } from '#/api/define-api'
import { requestClient } from '#/api/request'
import { dayjs } from '#/utils'

export const adsWeekReportApi = defineApi('operation', ({ prefix }) => {
  /**
   * 账户类型周消耗变化
   * POST /gobestads/operation/adsWeekReport/accountTypeWeek
   */
  function getAccountTypeWeek(params: AdsWeekAccountTypeWeekParams) {
    const dateRange = params.dateRange.map((date) =>
      dayjs(date).format('YYYY-MM-DD'),
    )

    return requestClient
      .post<AdsWeekAccountTypeWeekResponse>(
        prefix('adsWeekReport/accountTypeWeek'),
        {
          dateRange,
          accountType: params.accountType,
        },
      )
      .then((res) => {
        const data = (res as AdsWeekAccountTypeWeekResponse) || { items: [] }
        return (
          data.items?.map((weekItem) => ({
            week: weekItem.week,
            items:
              weekItem.items?.map((item) => ({
                accountType: item.accountType,
                spend: item.spend,
                change: item.change,
                changeRatio: item.changeRatio,
              })) || [],
          })) || []
        )
      })
  }

  /**
   * 行业周消耗变化
   * POST /gobestads/operation/adsWeekReport/industryWeek
   */
  function getIndustryWeek(params: AdsWeekIndustryWeekParams) {
    const dateRange = params.dateRange.map((date) =>
      dayjs(date).format('YYYY-MM-DD'),
    )

    return requestClient
      .post<AdsWeekIndustryWeekResponse>(prefix('adsWeekReport/industryWeek'), {
        dateRange,
        industry: params.industry,
      })
      .then((res) => {
        const data = (res as AdsWeekIndustryWeekResponse) || { items: [] }
        return (
          data.items?.map((weekItem) => ({
            week: weekItem.week,
            items:
              weekItem.items?.map((item) => ({
                industry: item.industry,
                spend: item.spend,
                change: item.change,
                changeRatio: item.changeRatio,
              })) || [],
          })) || []
        )
      })
  }

  /**
   * 新增客户 TOP20
   * POST /gobestads/operation/adsWeekReport/customerWeekChangeTop
   */
  function getCustomerWeekChangeTop(
    params: AdsWeekCustomerWeekChangeTopParams,
  ) {
    const dateRange = params.dateRange.map((date) =>
      dayjs(date).format('YYYY-MM-DD'),
    )

    return requestClient
      .post<AdsWeekCustomerWeekChangeTopResponse>(
        prefix('adsWeekReport/customerWeekChangeTop'),
        {
          dateRange,
        },
      )
      .then((res) => {
        const data = (res as AdsWeekCustomerWeekChangeTopResponse) || {
          riseTop20: [],
          declineTop20: [],
          byBD: [],
          byBDAndLevel: [],
        }
        return {
          riseTop20:
            data.riseTop20?.map((item) => ({
              customerId: item.customerId,
              companyName: item.companyName,
              operatorOpenId: item.operatorOpenId,
              managerOpenId: item.managerOpenId,
              spend: item.spend,
              change: item.change,
              changeRatio: item.changeRatio,
            })) || [],
          declineTop20:
            data.declineTop20?.map((item) => ({
              customerId: item.customerId,
              companyName: item.companyName,
              operatorOpenId: item.operatorOpenId,
              managerOpenId: item.managerOpenId,
              spend: item.spend,
              change: item.change,
              changeRatio: item.changeRatio,
            })) || [],
          byBD:
            data.byBD?.map((item) => ({
              managerOpenId: item.managerOpenId,
              riseTop20:
                item.riseTop20?.map((row) => ({
                  customerId: row.customerId,
                  companyName: row.companyName,
                  operatorOpenId: row.operatorOpenId,
                  managerOpenId: row.managerOpenId,
                  spend: row.spend,
                  change: row.change,
                  changeRatio: row.changeRatio,
                })) || [],
              declineTop20:
                item.declineTop20?.map((row) => ({
                  customerId: row.customerId,
                  companyName: row.companyName,
                  operatorOpenId: row.operatorOpenId,
                  managerOpenId: row.managerOpenId,
                  spend: row.spend,
                  change: row.change,
                  changeRatio: row.changeRatio,
                })) || [],
            })) || [],
          byBDAndLevel:
            data.byBDAndLevel?.map((item) => ({
              managerOpenId: item.managerOpenId,
              customerLevel: item.customerLevel,
              riseTop20:
                item.riseTop20?.map((row) => ({
                  customerId: row.customerId,
                  companyName: row.companyName,
                  operatorOpenId: row.operatorOpenId,
                  managerOpenId: row.managerOpenId,
                  spend: row.spend,
                  change: row.change,
                  changeRatio: row.changeRatio,
                })) || [],
              declineTop20:
                item.declineTop20?.map((row) => ({
                  customerId: row.customerId,
                  companyName: row.companyName,
                  operatorOpenId: row.operatorOpenId,
                  managerOpenId: row.managerOpenId,
                  spend: row.spend,
                  change: row.change,
                  changeRatio: row.changeRatio,
                })) || [],
            })) || [],
        }
      })
  }

  /**
   * 客户周度消耗分页列表
   * POST /gobestads/operation/adsWeekReport/customerWeekChangePage
   */
  function getCustomerWeekChangePage(
    params: AdsWeekCustomerWeekChangePageParams,
  ) {
    const dateRange = params.condition.dateRange.map((date) =>
      dayjs(date).format('YYYY-MM-DD'),
    )

    return requestClient
      .post<AdsWeekCustomerWeekChangePageResponse>(
        prefix('adsWeekReport/customerWeekChangePage'),
        {
          pageIndex: params.pageIndex,
          pageSize: params.pageSize,
          sortBy: params.sortBy,
          orderBy: params.orderBy,
          condition: {
            dateRange,
            customerId: params.condition.customerId,
            companyName: params.condition.companyName,
            customerLevel: params.condition.customerLevel,
            managerOpenId: params.condition.managerOpenId,
            operatorOpenId: params.condition.operatorOpenId,
            sortWeekIndex: params.condition.sortWeekIndex,
          },
        },
      )
      .then((res) => {
        const data = (res as AdsWeekCustomerWeekChangePageResponse) || {
          weeks: [],
          list: [],
          total: 0,
          pageIndex: params.pageIndex,
          pageSize: params.pageSize,
        }

        return {
          weeks: data.weeks || [],
          list:
            data.list?.map((item) => ({
              rowId: item.rowId,
              rowType: item.rowType,
              customerId: item.customerId,
              companyName: item.companyName,
              customerLevel: item.customerLevel,
              registeredAt: item.registeredAt,
              firstSpendAt: item.firstSpendAt,
              mediaPlatform: item.mediaPlatform,
              operatorOpenId: item.operatorOpenId,
              managerOpenId: item.managerOpenId,
              weekSpends: item.weekSpends,
              change: item.change,
              changeRatio: item.changeRatio,
              children: item.children?.map((child) => ({
                rowId: child.rowId,
                rowType: child.rowType,
                customerId: child.customerId,
                companyName: child.companyName,
                customerLevel: child.customerLevel,
                registeredAt: child.registeredAt,
                firstSpendAt: child.firstSpendAt,
                mediaPlatform: child.mediaPlatform,
                operatorOpenId: child.operatorOpenId,
                managerOpenId: child.managerOpenId,
                weekSpends: child.weekSpends,
                change: child.change,
                changeRatio: child.changeRatio,
              })),
            })) || [],
          total: data.total,
          pageIndex: data.pageIndex,
          pageSize: data.pageSize,
        }
      })
  }

  return {
    getAccountTypeWeek,
    getIndustryWeek,
    getCustomerWeekChangeTop,
    getCustomerWeekChangePage,
  }
})
